// The `MOST LOOKED-UP TERMS` card on frame 07 (33:1370) — a Card whose body is
// four `Data/ListRow`s, 64px tall on a 72px pitch.
//
// NO SCORING MATHS LIVES HERE. `/dashboards/{org}/terms` already returns
// `top_terms` ranked by usage; `rankByLookups` is a defensive re-sort of the
// server's ranking, not a second opinion about it. The byte-identical copy in
// `dashboard/ConfusionLeaderboard.tsx` was deleted with the rest of the
// pre-PRD-18 dashboard, so this is now the only one.
//
// It used to rank on `confusion_score` — `hover_count x dwell + 5 x
// abandonment` — which summed two opposite behaviours (careful study and
// bouncing off) into one number on a weight nobody tuned. That metric is gone.
// The card now shows look-ups per visit: `hover_count / unique_sessions`, both
// directly observed. `1.0` means every visit that opened the term opened it
// once and moved on; above that, readers came back to it inside the visit.

import { Badge, Card, CardHeader, ErrorState, Icon, List, ListRow } from "../../ui";
import { hrefs } from "../../shell/hrefs";
import type { TermMetric } from "../../dashboard/api";
// THE SAME MEASURE AND THE SAME HELPERS THE ANALYTICS LEADERBOARD USES.
// This card and `screens/analytics/MostLookedUp.tsx` draw the same four terms
// on adjacent screens; a second implementation of "look-ups per visit" would
// drift, and two screens disagreeing about one term is a credibility bug.
// `format.ts` owns the computation, the ordering and the rendering.
import {
  compareByLookups,
  formatLookupRate,
} from "../analytics/format";
import { LOOKUPS_PER_VISIT_WHY, NO_LOOKUPS_WHY } from "../analytics/metricLabels";
import { WINDOW_DAYS } from "./api";

/** The drawn card holds four rows and the card's height is drawn from them.
 *  480 terms must never render flat, so the count line states what was cut. */
export const LOOKUP_ROWS = 4;

const CARD_LABEL = "Most looked-up terms";

/** Absent, never zero. A term no visit opened has no rate to divide — which is
 *  a different claim from a rate of zero, and the card must not make the wrong
 *  one. `compareByLookups` puts those rows LAST rather than ranking them as if
 *  they scored 0, which would sort them below terms readers genuinely opened
 *  once per visit. They are still shown, carrying NOT_MEASURED. */
function rankByLookups(terms: TermMetric[]): TermMetric[] {
  return [...terms].sort((a, b) =>
    compareByLookups(a.lookups_per_session, b.lookups_per_session),
  );
}

/** The drawn pills tint the head of the ranking and leave the tail neutral. */
function toneFor(rate: number | null, top: number): "flagged" | "neutral" {
  return rate !== null && top > 0 && rate >= 0.6 * top ? "flagged" : "neutral";
}

/** A doc-local term id (`t04`) names a different term in every document, so the
 *  detail screen is a meaningless mixture without the doc.
 *
 *  THE PARAM IS `doc`, NOT `doc_id`. This card shipped writing `?doc_id=` while
 *  `routes/dashboard.$orgSlug.terms.$termId.tsx`'s `validateSearch` reads `doc`
 *  — so the scope was dropped silently and the detail screen's tiles stopped
 *  agreeing with the row that was clicked. `search` is passed as an object so
 *  the router, not a template literal, owns the encoding. */
function termSearch(term: TermMetric): Record<string, unknown> {
  return { doc: term.doc_id, days: WINDOW_DAYS };
}

export type MostLookedUpCardProps = {
  orgSlug: string;
  terms: TermMetric[];
  /** The population the ranking was drawn from — terms with reader activity in
   *  the window, per `GET /dashboards/{org}/sufficiency`. Null when that
   *  credential cannot reach it; the fetched pool is then the honest total. */
  rankedTotal: number | null;
  failed?: boolean;
};

export function MostLookedUpCard({
  orgSlug,
  terms,
  rankedTotal,
  failed = false,
}: MostLookedUpCardProps) {
  const ranked = rankByLookups(terms);
  const shown = ranked.slice(0, LOOKUP_ROWS);
  const top = ranked[0]?.lookups_per_session ?? 0;
  const total = rankedTotal ?? ranked.length;

  const header = (
    <CardHeader
      label={CARD_LABEL}
      actions={
        shown.length > 0 ? (
          <span className="maisight-overview-count type-labelSm">
            {`Top ${shown.length} of ${total} terms with reader activity`}
          </span>
        ) : undefined
      }
    />
  );

  if (failed) {
    return (
      <Card header={header}>
        <ErrorState headline="Reader analytics are unavailable right now" />
      </Card>
    );
  }

  if (shown.length === 0) {
    return (
      <Card header={header}>
        <p className="maisight-overview-note type-bodyS">
          {`No term activity in the last ${WINDOW_DAYS} days.`}
        </p>
      </Card>
    );
  }

  return (
    <Card header={header}>
      <List label={CARD_LABEL}>
        {shown.map((term) => (
          <ListRow
            key={`${term.doc_id}:${term.term_id}`}
            to={hrefs(orgSlug).term(term.term_id)}
            search={termSearch(term)}
            title={term.term_id}
            meta={`${term.doc_id} · ${term.hover_count} look-ups · ${term.unique_sessions} visits`}
            trailing={
              <>
                {/* The no-verdict treatment `FixAndProve` uses: a neutral
                  * badge carrying the shared NOT_MEASURED string, plus
                  * `data-measured` so the absence is machine-checkable.
                  * Never "0.0 per visit". */}
                <Badge tone={toneFor(term.lookups_per_session, top)}>
                  <span
                    className="maisight-analytics-metric"
                    data-measured={term.lookups_per_session === null ? "no" : "yes"}
                    title={
                      term.lookups_per_session === null
                        ? NO_LOOKUPS_WHY
                        : LOOKUPS_PER_VISIT_WHY
                    }
                  >
                    {formatLookupRate(term.lookups_per_session)}
                  </span>
                </Badge>
                <Icon name="chevron-right" />
              </>
            }
          />
        ))}
      </List>
    </Card>
  );
}
